import React, { ReactNode, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Col, Drawer, Menu, Row } from 'antd'
import classNames from 'classnames'

import { WizardStep, StepStatus as WizardStepStatus } from 'core/domain/wizard/wizard'

import Icon from 'common/components/general/Icon'

import { Button } from 'common/components'

import { NavItemProps } from './models'

const DEFAULT_NAVIGATION_ITEM_INDEX = 0

declare type StepStatus = 'complete' | 'active' | 'wait' | 'disable'

interface SidebarNavigationProps {
  navigationItems: WizardStep[]
  hasDirtyData: boolean
  setCurrentNavigationItem: (item: NavItemProps) => void
  bindGoBack: (goBack: () => void) => void
  bindGoNext: (goNext: () => void) => void
  bindGetIndexOfActiveNavigationItem: (getIndexOfActiveNavigationItem: (items: WizardStep[]) => number) => void
  bindSetEditingNavigationItem: (setEditingNavigationItem: (item: string | undefined) => void) => void
  showDisclaimerModal: (onCancel: () => void, translate: any) => void
  navItemMapping: NavItemProps[]
  isMobile?: boolean
}

export const SidebarNavigation: React.FC<SidebarNavigationProps> = ({
  bindGoBack,
  bindGoNext,
  bindGetIndexOfActiveNavigationItem,
  bindSetEditingNavigationItem,
  navigationItems,
  hasDirtyData,
  showDisclaimerModal,
  isMobile = false,
  navItemMapping,
  setCurrentNavigationItem,
}) => {
  const [editingNavigationItem, setEditingNavigationItem] = useState<string | undefined>(undefined)
  const [isDrawerVisible, setIsDrawerVisible] = useState<boolean>(false)
  const [activeKeys, setActiveKeys] = useState<string[] | undefined>()
  const { t: translate } = useTranslation()
  const navigationItemsGenerator = (items: WizardStep[]): ReactNode => {
    const activeNavigationItemIndex = getIndexOfActiveNavigationItem(items)
    return (
      <Menu
        onClick={(state) => {
          if (hasDirtyData) {
            showDisclaimerModal(() => {
              redirectToNavigationItem(state.key)
            }, translate)
          } else {
            redirectToNavigationItem(state.key)
          }
        }}
        selectedKeys={activeKeys}
      >
        {items.map((item, index) => {
          const categoryItemStatus = getNavigationItemStatus(item)
          return (
            <Menu.Item
              key={item.id}
              className={classNames({
                pointer: true,
                menu__item_active: activeNavigationItemIndex === index,
                menu__item_complete: categoryItemStatus === 'complete',
                menu__item_disabled: categoryItemStatus === 'wait',
              })}
              disabled={categoryItemStatus === 'disable'}
            >
              {getTitleContentOfNavigationItem(item)}
            </Menu.Item>
          )
        })}
      </Menu>
    )
  }

  const getIndexOfActiveNavigationItem = (items: WizardStep[]): number => {
    try {
      if (editingNavigationItem) {
        const navItemIndex = items.findIndex((item) => item.id === editingNavigationItem)
        if (navItemIndex === -1) throw new Error('Navigation Item not found')
        const navItem = items[navItemIndex]
        if (navItem.status === WizardStepStatus.Active || navItem.status === WizardStepStatus.Complete)
          return navItemIndex
        else setEditingNavigationItem(undefined)
      }

      const activeNavigationItemIndex = items.findIndex((item) => item.status === WizardStepStatus.Active)

      if (activeNavigationItemIndex === -1) return DEFAULT_NAVIGATION_ITEM_INDEX
      return activeNavigationItemIndex
    } catch (e) {
      console.log(e)
      return DEFAULT_NAVIGATION_ITEM_INDEX
    }
  }

  const getNavigationItemStatus = (navigationItem: WizardStep): StepStatus => {
    if (editingNavigationItem === navigationItem.id) {
      return 'active'
    } else if (editingNavigationItem && navigationItem.status === WizardStepStatus.Active) {
      return 'wait'
    } else if (navigationItem.status === WizardStepStatus.Complete) {
      return 'complete'
    } else if (navigationItem.status === WizardStepStatus.Active) {
      return 'active'
    } else {
      return 'disable'
    }
  }

  const getTitleContentOfNavigationItem = (navigationItem: WizardStep): ReactNode => {
    const navigationItemProps = navItemMapping.find((item) => item.key === navigationItem.id)
    return (
      <Row gutter={8} className={'step_content'}>
        <Col>
          {getStepStatusIcon(navigationItem)}
          <Icon type={navigationItemProps ? navigationItemProps.icon : ''} />
        </Col>
        <Col>
          <h5 className="step_title">{navigationItem.name}</h5>
        </Col>
      </Row>
    )
  }

  const getStepStatusIcon = (item: WizardStep): ReactNode => {
    if (item.status === WizardStepStatus.Complete) {
      return (
        <div className="step_status step_status_completed">
          <Icon className="far fa-check" />
        </div>
      )
    } else if (item.status === WizardStepStatus.Disabled) {
      return <div className="step_status step_status_disabled">{item.priority + 1}</div>
    } else {
      return <div className="step_status step_status_active">{item.priority + 1}</div>
    }
  }

  const redirectToNavigationItem = (id: string) => {
    try {
      const navigationItemProps = navItemMapping.find((navItem) => navItem.key === id)
      if (navigationItemProps === undefined) throw new Error('Navigation Item Props not found')
      setCurrentNavigationItem(navigationItemProps)
      setEditingNavigationItem(id)
      setActiveKeys([id])
    } catch (e) {
      console.log(e)
    }
  }

  const goBack = () => {
    try {
      const currentNavigationItemIndex = getIndexOfActiveNavigationItem(navigationItems)
      const item = navigationItems[currentNavigationItemIndex]
      if (item === undefined) throw new Error('Navigation item not found')

      if (currentNavigationItemIndex > 0) {
        let nextItem = navigationItems[currentNavigationItemIndex - 1]
        redirectToNavigationItem(nextItem.id)
      } else {
        redirectToNavigationItem(navigationItems[DEFAULT_NAVIGATION_ITEM_INDEX].id)
      }
    } catch (e) {
      console.log(e)
      redirectToNavigationItem(navigationItems[DEFAULT_NAVIGATION_ITEM_INDEX].id)
    }
  }

  const goNext = () => {
    try {
      const currentNavigationItemIndex = getIndexOfActiveNavigationItem(navigationItems)
      const item = navigationItems[currentNavigationItemIndex]
      if (item === undefined) throw new Error('Navigation item not found')
      item.status = WizardStepStatus.Complete
      if (currentNavigationItemIndex < navigationItems.length - 1) {
        let nextItem = navigationItems[currentNavigationItemIndex + 1]
        if (nextItem.status === WizardStepStatus.Disabled) {
          nextItem.status = WizardStepStatus.Active
        }
        redirectToNavigationItem(nextItem.id)
      } else {
        redirectToNavigationItem(navigationItems[DEFAULT_NAVIGATION_ITEM_INDEX].id)
      }
    } catch (e) {
      console.log(e)
      redirectToNavigationItem(navigationItems[DEFAULT_NAVIGATION_ITEM_INDEX].id)
    }
  }

  bindGetIndexOfActiveNavigationItem(getIndexOfActiveNavigationItem)
  bindSetEditingNavigationItem(setEditingNavigationItem)
  bindGoNext(goNext)
  bindGoBack(goBack)

  return isMobile ? (
    <div className="admin-signup-sticky-container pt-24">
      <Button
        className="admin-sign-up-drawer-button"
        onClick={() => {
          setIsDrawerVisible(true)
        }}
        type="text"
      >
        <Icon type="far fa-indent" fontAwesome />
      </Button>
      <Drawer
        rootClassName="admin-signup-mobile-drawer sign_up"
        width={240}
        open={isDrawerVisible}
        onClose={() => {
          setIsDrawerVisible(false)
        }}
        placement="left"
      >
        <div className="ee_sidebar_navigation-menu">{navigationItemsGenerator(navigationItems)}</div>
      </Drawer>
    </div>
  ) : (
    <div className="ee_sidebar_navigation-menu">{navigationItemsGenerator(navigationItems)}</div>
  )
}
